/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC } from "react"
import { Card, Row, Col, Dropdown } from "@duxton-web/ui"
import { MoreVerticalUniversal } from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Department } from "types/models/menu"
import { SortableItemList } from "components/common"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { getUCMConfig } from "utils/config"
import { debounceSortHandler } from "utils/misc"
import { useTranslationsMap } from "common/utils/i18n"

import { sortMenuItemNewLogic } from "utils/sort_helper"
import commonStyles from "styles/common.modules.css"
import { useMenuEditorStatus } from "../../hooks/useMenuEditorStatus"
import { MenuDepartmentRow } from "./MenuDepartmentRow"
import { MenuDepartmentRowLoading } from "./MenuDepartmentRowLoading"
import { useMenuOverviewDepartments } from "../hooks/useMenuOverviewDepartments"

interface IProps {
  hasAction: boolean
}

export const MenuOverviewDepartments: FC<IProps> = ({ hasAction }: IProps) => {
  const dispatch = useDispatch()

  const { isEditAllowed } = useMenuEditorStatus()
  const { filteredDepartments, isLoading } = useMenuOverviewDepartments()

  const {
    overview: { section: selectedSection },
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    isUCS,
  } = useSelector((state: AppState) => state?.menu)

  const selectedDepartment = useSelector(
    (state: AppState) => state?.menu?.overview?.department,
  )

  const handleMenuClick = (key: string) => {
    let VALUE = null

    switch (key) {
      case "rearrange":
        dispatch(
          actionCreator.menu.toggleDrawer({
            showSortDepartments: true,
          }),
        )
        VALUE = "REARRANGE"
        break
      case "multiple":
        dispatch(
          actionCreator.menu.toggleDrawer({
            showItemSelection: true,
          }),
        )
        VALUE = "SELECT_MULTIPLE"
        break
      default:
        break
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickDepartmentMore,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        NUM_DEPARTMENT: filteredDepartments?.length ?? 0,
        VALUE,
      },
    })
  }

  const handleSelectDepartment = (department: Department) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickDepartmentName,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        DEPARTMENT_ID: department.itemClassID,
      },
    })

    dispatch(actionCreator.menu.selectDepartment(department))
  }

  const renderRow = (department: Department) => {
    return <MenuDepartmentRow department={department} />
  }

  const textMap = useTranslationsMap({
    rearrange: "menu.actions.rearrange",
    selectMultiple: "menu.actions.selectMultiple",
    sortCategoriesSuccess: "menu.notifications.sortCategories.successMessage",
    categories: "menu.headings.categories",
  })

  const onSortEnd = debounceSortHandler(
    (sortedDepartments: Department[], newIndex: number) => {
      const sectionID = selectedSection?.sectionID ?? ""

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewRearrangeDepartment,
        action: EventAction.submitRearrangeDepartment,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          SECTION_ID: sectionID ?? "",
        },
      })

      const sorts = isUCS
        ? sortMenuItemNewLogic({
            newIndex,
            newList: sortedDepartments,
            sortKey: "sortOrder",
            itemKey: "itemClassID",
          })
        : sortedDepartments.map((department, index) => ({
            resourceID: department.itemClassID ?? "",
            sortOrder: sortedDepartments.length - index,
          }))

      dispatch(
        actionCreator.menu.sortDepartments({
          merchantID,
          sorts,
          successMessage: textMap.sortCategoriesSuccess,
          menuGroupID,
          merchantGroupID,
        }),
      )
    },
  )

  if (isLoading) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.left}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.categories}</Col>
          </Row>
        }
      >
        <MenuDepartmentRowLoading />
      </Card>
    )
  }

  return (
    <Card
      className={`${commonStyles.menuTableCard} ${commonStyles.left}`}
      title={
        <Row justify="space-between">
          <Col>{textMap.categories}</Col>
          {hasAction && getUCMConfig("enableTableMenuButton") && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{
                  items: [
                    ...(isEditAllowed &&
                    !(
                      filteredDepartments?.length === 0 ||
                      selectedDepartment === null
                    )
                      ? [
                          {
                            key: "rearrange",
                            label: textMap.rearrange,
                            onClick: () => handleMenuClick("rearrange"),
                          },
                        ]
                      : []),
                    {
                      key: "multiple",
                      label: textMap.selectMultiple,
                      onClick: () => handleMenuClick("multiple"),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      <SortableItemList
        items={filteredDepartments}
        renderRowContent={renderRow}
        useDragHandle
        isItemSelected={(department: Department) =>
          department.itemClassID === selectedDepartment?.itemClassID
        }
        onRowClick={(_, department: Department) =>
          handleSelectDepartment(department)
        }
        draggable={getUCMConfig("enableDraggableList")}
        onSort={({ newList, newIndex }) => {
          onSortEnd(newList, newIndex)
        }}
      />
    </Card>
  )
}
